import React, { ReactNode, memo, useRef, useEffect, useState } from "react";
import {
  Center,
  Loader,
  Space,
  Table,
  Text,
  Box,
  Group,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
  Anchor,
  Affix,
} from "@mantine/core";
import { TokenDisplay } from "../Components/TokenDisplay";
import {
  IFlowNetProfit,
  IFlowProcess,
  IFlowToken,
  IFlowTrade,
  TransactionFlow,
} from "../api/type";
import { ContractDisplay } from "../Components/ContractDisplay";
import { AmountDisplay } from "../Components/AmountDisplay";
import { TxDisplay } from "../Components/TxDisplay";
import { shrink, useIsSmallScreen } from "../Components/Utils";
import { Td, Th, ThText } from "../Components/EpTable";
import { FaGasPump } from "react-icons/fa";
import { ImHammer } from "react-icons/im";
import { EpAlert } from "../Components/EpMessage";
import { Info } from "../Components/EpIcon";
import { Link } from "../Components/Link";
import Icon from "../Components/Icon";
import { useGlobalStatusContext } from "../GlobalStore";
import { useElementSize, useWindowScroll } from "@mantine/hooks";
import { getValue, fetchAndActivate } from "firebase/remote-config";
import { EpBadge } from "../Components/EpBadge";
import {
  AnalyseTransaction,
  AnalyseTransactionResponseJson,
} from "../EigenTx/api/type";
import { useQuery } from "react-query";

const PROFIT_TAG_WIDTH = 40;
const PROFIT_ADDRESS_WIDTH = 250;
export const PROFIT_TOKEN_WIDTH = 170;
const VIRTUAL_PROFIT_TOKEN_WIDTH = PROFIT_TOKEN_WIDTH;

const liquidationSort = (
  tradeList: Array<IFlowTrade>,
  addressToLabels?: {
    [address: string]: Array<string>;
  },
  addressRoles?: {
    [address: string]: string;
  }
): Array<IFlowTrade> => {
  let list = [...tradeList];
  const sortArr1 = ["from", "to"];
  const sortArr2 = ["liquidator", "borrower", "miner"];
  const newList: Array<IFlowTrade> = [];
  let nextList: Array<IFlowTrade> = [];

  sortArr1.forEach((sortKey) => {
    let idxArr: Array<number> = [];

    list.forEach((trade: IFlowTrade, idx: number) => {
      const labels = addressToLabels?.[trade.address]?.map((tag) =>
        tag.toLowerCase()
      );

      if (labels?.includes(sortKey)) {
        idxArr.push(idx);
        newList.push(trade);
      } else {
        nextList.push(trade);
      }
    });

    list = nextList;
    nextList = [];
  });

  sortArr2.forEach((sortKey) => {
    let idxArr: Array<number> = [];

    list.forEach((trade: IFlowTrade, idx: number) => {
      const tag = addressRoles?.[trade.address] || "";

      if (tag.toLowerCase() === sortKey) {
        idxArr.push(idx);
        newList.push(trade);
      } else {
        nextList.push(trade);
      }
    });

    list = nextList;
    nextList = [];
  });

  return [...newList, ...list];
};

function debounce(func: any, timeout = 1000) {
  let timer: any = null;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply();
    }, timeout);
  };
}

export const TransactionTablePro = memo(function ({
  overview,
  data,
  isLoading,
}: {
  overview?: {
    blockNumber: number;
  };
  data?: TransactionFlow;
  isLoading: boolean;
}) {
  const { chain } = useGlobalStatusContext();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const isSmallScreen = useIsSmallScreen();
  const [scroll] = useWindowScroll();
  const size = useElementSize();

  const tableRef = useRef<any>();
  const headerRef = useRef<any>();
  const [builderMap, setBuilderMap] = useState<any>({});

  const handleOnScrollCapture = (index: number) => {
    const elRefs = [headerRef, tableRef];
    if (headerRef.current && tableRef.current) {
      elRefs.forEach((el, idx) => {
        if (idx !== index) {
          el.current.scrollLeft = elRefs[index]?.current.scrollLeft;
        }
      });
    }
  };

  useEffect(() => {
    fetchAndActivate(window.remoteConfig).then(() => {
      const builderMapStr = getValue(
        window.remoteConfig,
        "builder_name_map"
      ).asString();

      if (builderMapStr) {
        try {
          const builderMap = JSON.parse(builderMapStr);
          setBuilderMap(builderMap);
        } catch (ex) {}
      }
    });
  }, []);

  const isHeaderFixed =
    size?.ref?.current?.offsetTop &&
    scroll.y > size?.ref?.current?.offsetTop - 10;

  useEffect(() => {
    if (isHeaderFixed) {
      setTimeout(() => {
        handleOnScrollCapture(1);
      }, 200);
    }
  }, [isHeaderFixed]);

  let txhash: string = "";
  data?.processList
    // .filter(p=>(p.affiliatedRole.toLowerCase() === "frontrun" || p.affiliatedRole.toLowerCase() === "backrun" ))
    .forEach((p) => (txhash += `${p.transactionHash},`));
  txhash = txhash.substring(0, txhash.length - 1);
  // console.log("TransactionTablePro txhash", txhash);
  let { data: analysisData, isLoading: isLoadingAnalysisData } = useQuery<
    Array<AnalyseTransaction | undefined>,
    AnalyseTransactionResponseJson
  >([
    "fetchAnalyseTransaction",
    {
      chain: "ALL",
      tx: txhash,
      enableCallStack: "on",
    },
  ]);
  // setAnalysisData(_analysisData)
  // console.log("TransactionTablePro analysisData", analysisData);
  if (isLoading || isLoadingAnalysisData) {
    return (
      <Center>
        <Loader className="mantine-loader" />
      </Center>
    );
  }
  if (data) {
    const {
      processList = [],
      addressToLabels = {},
      lpIcons = {},
      addressRoles = {},
      attackedLPs = [],
      addressLinkType = {},
      tokenLinkType = {},
      txPositions = {},
    } = data;
    const txType = data.type;

    if (!processList.length) {
      return <EpAlert>Data Not Found!</EpAlert>;
    }

    const attacker1AddressHash: { [address: string]: IFlowTrade } = {};
    const attacker2AddressHash: { [address: string]: IFlowTrade } = {};
    const victimAddressHash: { [address: string]: IFlowTrade } = {};

    const initiator1AddressHash: { [address: string]: IFlowTrade } = {};
    const initiator2AddressHash: { [address: string]: IFlowTrade } = {};
    const affectedAddressHash: { [address: string]: IFlowTrade } = {};

    const tbodys: Array<ReactNode> = [];

    const tokenHash: { [address: string]: IFlowToken } = {};
    const tokenSymbolCountHash: { [symbol: string]: number } = {};

    // 过滤掉地址的数据amount全是0的行
    const totalRevenues =
      data?.totalRevenues?.filter((item) =>
        item?.revenues?.some((revenue) => revenue?.amount !== "0")
      ) || [];
    const totalProfits =
      data?.totalProfits?.filter((item) =>
        item?.revenues?.some((revenue) => revenue?.amount !== "0")
      ) || [];
    const totalCosts =
      data?.totalCosts?.filter((item) =>
        item?.revenues?.some((revenue) => revenue?.amount !== "0")
      ) || [];

    const sampToken = processList[0].tradeList[0]?.tradeRevenues[0].token;
    // merge
    analysisData?.forEach((tx, idx) => {
      tx?.combinedTransferTable.rows.forEach((transfer) => {
        transfer.cells
          .filter(
            (detail) => detail.token.address.includes("virtualLiquidity")
            // detail => detail.token.address.includes("virtualOwed")
            // detail => detail.token.address.includes("virtualLiquidity") || detail.token.address.includes("virtualOwed")
          )
          .forEach(
            // add to
            (detail) => {
              const tradeRevenues = processList[idx].tradeList.filter((trade) =>
                transfer.address.includes(trade.address)
              )[0]?.tradeRevenues;
              if (
                tradeRevenues.filter(
                  (r) => r.token.address === detail.token.address
                ).length === 0
              ) {
                processList[idx].tradeList
                  .filter((trade) =>
                    transfer.address.includes(trade.address)
                  )[0]
                  ?.tradeRevenues.push({
                    amount: detail.amount,
                    type: "Revenue",
                    amountDecimal: detail.amount,
                    token: {
                      ...detail.token,
                      chain: sampToken.chain,
                      tokenSpecEnum: "virtual",
                    },
                  });
              }
            }
          );
      });
    });

    processList.forEach((process: IFlowProcess, idx: number) => {
      process.tradeList.forEach((trade: IFlowTrade) => {
        trade.tradeRevenues.forEach((revenue) => {
          tokenHash[revenue.token.address] = revenue.token;
        });
        if (txType?.toLowerCase() === "sandwich") {
          if (process.tag?.toLowerCase() === "attacker") {
            if (idx === 0) {
              attacker1AddressHash[trade.address] = trade;
            } else if (idx === 2) {
              attacker2AddressHash[trade.address] = trade;
            }
          } else if (process.tag?.toLowerCase() === "victim") {
            victimAddressHash[trade.address] = trade;
          }
        }

        if (txType?.toLowerCase() === "jit") {
          if (process.tag?.toLowerCase() === "initiator") {
            if (idx === 0) {
              initiator1AddressHash[trade.address] = trade;
            } else if (idx === 2) {
              initiator2AddressHash[trade.address] = trade;
            }
          } else if (process.tag?.toLowerCase() === "affected") {
            affectedAddressHash[trade.address] = trade;
          }
        }
      });
      // console.log("affectedAddressHash", affectedAddressHash);
      // 根据角色等排序
      if (txType?.toLowerCase() === "liquidation") {
        const sortList = liquidationSort(
          process.tradeList,
          addressToLabels,
          addressRoles
        );
        process.tradeList = sortList;
      }
    });

    [
      ...(totalCosts ?? []),
      ...(totalProfits ?? []),
      ...(totalRevenues ?? []),
    ].forEach((net) => {
      net.revenues.forEach((revenue) => {
        tokenHash[revenue.token.address] = revenue.token;
      });
    });
    const tokenArr: Array<IFlowToken> = Object.values(tokenHash);

    tokenArr.forEach((token) => {
      tokenSymbolCountHash[token.symbol] =
        (tokenSymbolCountHash[token.symbol] ?? 0) + 1;
    });

    processList.forEach((process: IFlowProcess, idx: number) => {
      const rows = process.tradeList.map((trade, idx) => (
        <tr key={`tr-trade-${trade.address}-${idx}`}>
          {txType?.toLowerCase() === "sandwich" && (
            <Td width={isSmallScreen ? `${PROFIT_TAG_WIDTH}px` : "auto"}>
              <Group sx={() => ({ alignItems: "center", width: 16 })}>
                {attackedLPs.includes(trade.address) ||
                (attackedLPs.length === 0 &&
                  victimAddressHash[trade.address] &&
                  attacker1AddressHash[trade.address] &&
                  attacker2AddressHash[trade.address]) ? (
                  <Tooltip label="Common LP" withArrow position="right">
                    <Box
                      sx={() => ({
                        display: "block",
                        borderRadius: "50%",
                        width: 16,
                        height: 16,
                        backgroundColor: "#FFEC99",
                      })}
                    />
                  </Tooltip>
                ) : trade.tag ? (
                  {
                    attacker: (
                      <Tooltip label="Attacker" withArrow position="right">
                        <Box>
                          <Icon name="icon-role" color="#F64E60" />
                        </Box>
                      </Tooltip>
                    ),
                    victim: (
                      <Tooltip label="Victim" withArrow position="right">
                        <Box>
                          <Icon
                            name="icon-role"
                            color={dark ? "#B5B5C3" : "#7E8299"}
                          />
                        </Box>
                      </Tooltip>
                    ),
                    middleattacker: (
                      <Tooltip
                        label="MiddleAttacker"
                        withArrow
                        position="right"
                      >
                        <Box>
                          <Icon name="icon-role" color="#8667BF" />
                        </Box>
                      </Tooltip>
                    ),
                    miner: (
                      <Tooltip
                        label={
                          chain.toLowerCase() === "bsc"
                            ? "Validator"
                            : "Builder"
                        }
                        withArrow
                        position="right"
                      >
                        <Box>
                          <ImHammer
                            style={{ color: "#54B498", fontSize: 19 }}
                          />
                        </Box>
                      </Tooltip>
                    ),
                  }[trade.tag?.toLowerCase()]
                ) : (
                  <Tooltip label="Other Token Flow" withArrow position="right">
                    <Box
                      sx={() => ({
                        display: "block",
                        borderRadius: "50%",
                        width: 16,
                        height: 16,
                        backgroundColor: "#E5E5E5",
                      })}
                    />
                  </Tooltip>
                )}
              </Group>
            </Td>
          )}
          {txType?.toLowerCase() === "jit" && (
            <Td width={isSmallScreen ? `${PROFIT_TAG_WIDTH}px` : "auto"}>
              <Group sx={() => ({ alignItems: "center", width: 16 })}>
                {attackedLPs.includes(trade.address) ||
                (attackedLPs.length === 0 &&
                  victimAddressHash[trade.address] &&
                  initiator1AddressHash[trade.address] &&
                  initiator2AddressHash[trade.address]) ? (
                  <Tooltip label="Common LP" withArrow position="right">
                    <Box
                      sx={() => ({
                        display: "block",
                        borderRadius: "50%",
                        width: 16,
                        height: 16,
                        backgroundColor: "#FFEC99",
                      })}
                    />
                  </Tooltip>
                ) : trade.tag ? (
                  {
                    initiator: (
                      <Tooltip label="Initiator" withArrow position="right">
                        <Box>
                          <Icon name="icon-role" color="#F64E60" />
                        </Box>
                      </Tooltip>
                    ),
                    affected: (
                      <Tooltip label="Affected" withArrow position="right">
                        <Box>
                          <Icon
                            name="icon-role"
                            color={dark ? "#B5B5C3" : "#7E8299"}
                          />
                        </Box>
                      </Tooltip>
                    ),
                    miner: (
                      <Tooltip
                        label={
                          chain.toLowerCase() === "bsc"
                            ? "Validator"
                            : "Builder"
                        }
                        withArrow
                        position="right"
                      >
                        <Box>
                          <ImHammer
                            style={{ color: "#54B498", fontSize: 19 }}
                          />
                        </Box>
                      </Tooltip>
                    ),
                  }[trade.tag?.toLowerCase()]
                ) : (
                  <Tooltip label="Other Token Flow" withArrow position="right">
                    <Box
                      sx={() => ({
                        display: "block",
                        borderRadius: "50%",
                        width: 16,
                        height: 16,
                        backgroundColor: "#E5E5E5",
                      })}
                    />
                  </Tooltip>
                )}
              </Group>
            </Td>
          )}
          <Td width={isSmallScreen ? `${PROFIT_ADDRESS_WIDTH}px` : "auto"}>
            <Group noWrap={!isSmallScreen} spacing={6}>
              <ContractDisplay
                address={trade.address}
                labelList={addressToLabels?.[trade.address]}
                lpIcon={lpIcons?.[trade.address]}
                chain={chain}
                blockNumber={overview?.blockNumber}
                {...{
                  [addressLinkType[trade.address]]: true,
                }}
              />
              {builderMap[trade.address.toLowerCase()] ? (
                <EpBadge text={builderMap[trade.address.toLowerCase()]} />
              ) : null}

              {addressRoles[trade.address] ? (
                <EpBadge text={addressRoles[trade.address]} />
              ) : null}
            </Group>
          </Td>
          {tokenArr.map((token) => {
            let td = (
              <Td
                width={isSmallScreen ? `${PROFIT_TOKEN_WIDTH}px` : "auto"}
                key={`td-${token.address}`}
              ></Td>
            );
            trade.tradeRevenues.forEach((revenue) => {
              if (token.address === revenue.token.address) {
                td = (
                  <Td
                    width={isSmallScreen ? `${PROFIT_TOKEN_WIDTH}px` : "auto"}
                    align="right"
                    key={`td-${token.address}`}
                  >
                    <AmountDisplay
                      value={parseFloat(
                        revenue.amountDecimal || revenue.amount
                      )}
                      digits={6}
                      hideZero
                      style={
                        trade.tag
                          ? {
                              color: dark ? theme.white : theme.colors.dark[6],
                              fontWeight: "bold",
                            }
                          : {
                              color: dark ? theme.white : theme.colors.dark[6],
                            }
                      }
                    />
                  </Td>
                );
              }
            });
            return td;
          })}
        </tr>
      ));

      const filterNetProfits = process.netProfits.filter(
        (profit) =>
          Math.abs(
            profit.revenues
              .map((revenue) =>
                parseFloat(revenue.amountDecimal || revenue.amount)
              )
              .sort((prev, next) => next - prev)[0]
          ) >= 0
      );

      tbodys.push(
        <tbody key={idx}>
          {process.transactionHash && (
            <tr>
              <td colSpan={tokenArr.length + 2} style={{ padding: 0 }}>
                <Box
                  sx={(theme) => ({
                    width: "100%",
                    padding: "10px 20px",
                    borderRadius: "8px 8px 0px 0px",
                    borderBottom:
                      theme.colorScheme === "dark"
                        ? "none"
                        : "1px solid #e9ecef",
                    marginTop: 4,
                    backgroundColor: process.affiliatedRole
                      ? {
                          frontrun:
                            theme.colorScheme === "dark"
                              ? theme.fn.rgba(theme.colors.red[7], 0.4)
                              : theme.colors.red[1],
                          backrun:
                            theme.colorScheme === "dark"
                              ? theme.fn.rgba(theme.colors.red[7], 0.4)
                              : theme.colors.red[1],
                          midfrontrun:
                            theme.colorScheme === "dark"
                              ? theme.fn.rgba(theme.colors.red[7], 0.4)
                              : theme.colors.red[1],
                          victim:
                            theme.colorScheme === "dark"
                              ? theme.fn.rgba(theme.colors.green[7], 0.4)
                              : theme.colors.green[1],
                          affected:
                            theme.colorScheme === "dark"
                              ? theme.fn.rgba(theme.colors.green[7], 0.4)
                              : theme.colors.green[1],
                          middleattacker:
                            theme.colorScheme === "dark"
                              ? "#533C7D"
                              : "#E9DCFF",
                        }[process.affiliatedRole?.toLowerCase()]
                      : "",
                  })}
                >
                  <Group spacing={20} className="chart-header">
                    <Anchor
                      component={Link}
                      to={`/eigentx/${process.transactionHash}`}
                      sx={{
                        textDecoration: "none !important",
                      }}
                    >
                      <Text
                        sx={(theme) => ({
                          fontSize: 14,
                          fontWeight: 700,
                          color:
                            theme.colorScheme === "dark"
                              ? theme.white
                              : theme.colors.dark[6],
                        })}
                      >
                        {{
                          MiddleAttacker: "Arbitrager",
                          MidFrontRun: "FrontRun&BackRun",
                        }[process?.affiliatedRole] ||
                          txType + " " + process?.affiliatedRole}
                      </Text>
                    </Anchor>
                    <TxDisplay
                      prefix="Tx: "
                      tx={process.transactionHash}
                      chain={chain}
                      browser
                    />

                    <Group noWrap spacing={5}>
                      <Text size="sm">
                        Position: {txPositions[process.transactionHash]}
                      </Text>
                      <Info content="Index Position of transaction in the block" />
                    </Group>
                  </Group>
                </Box>
              </td>
            </tr>
          )}
          {rows}
          {filterNetProfits.length && processList.length > 1 ? (
            <tr>
              <Td colSpan={tokenArr.length + 1}>
                <ThText>NET</ThText>
              </Td>
            </tr>
          ) : (
            <></>
          )}
          {processList.length > 1
            ? filterNetProfits
                .sort((profit) => (profit.tag ? -1 : 0))
                .map((profit, idx) => {
                  const tds = tokenArr.map((token) => {
                    let td = <Td key={`td-${token.address}`}></Td>;
                    profit.revenues.forEach((revenue) => {
                      if (revenue.token.address === token.address) {
                        td = (
                          <Td align="right" key={`td-${token.address}`}>
                            <AmountDisplay
                              value={parseFloat(
                                revenue.amountDecimal || revenue.amount
                              )}
                              digits={6}
                              hideZero
                              style={{
                                color: "#383838",
                                fontWeight: "bold",
                              }}
                            />
                          </Td>
                        );
                      }
                    });
                    return td;
                  });
                  return (
                    <tr key={`tr-nets-${idx}-${profit.address}`}>
                      <Td>
                        <ContractDisplay
                          address={profit.address}
                          labelList={addressToLabels?.[profit.address]}
                          lpIcon={lpIcons?.[profit.address]}
                          chain={chain}
                          tag={profit.tag}
                          {...{
                            [addressLinkType[profit.address]]: true,
                          }}
                        />
                      </Td>
                      {tds}
                    </tr>
                  );
                })
            : null}
        </tbody>
      );
      if (filterNetProfits.length && processList.length > 1)
        tbodys.push(
          <tbody key={"sp" + idx}>
            <tr
              style={{
                height: 40,
                borderTop: dark ? "1px dashed #373A40" : "1px dashed #dee2e6",
              }}
            ></tr>
          </tbody>
        );
    });

    const headers = (
      <tr>
        {txType?.toLowerCase() === "sandwich" ||
        txType?.toLowerCase() === "jit" ? (
          <Th width={isSmallScreen ? `${PROFIT_TAG_WIDTH}px` : "auto"}></Th>
        ) : null}
        <Th width={isSmallScreen ? `${PROFIT_ADDRESS_WIDTH}px` : "auto"}>
          <ThText>
            {(txType?.toLowerCase() === "sandwich" ||
              txType?.toLowerCase() === "jit") &&
              "Address"}
          </ThText>
        </Th>
        {tokenArr.map((token) => (
          <Th
            key={`th-${token.address}`}
            style={{ textAlign: "right", fontWeight: 400 }}
            width={isSmallScreen ? `${PROFIT_TOKEN_WIDTH}px` : "auto"}
          >
            <Group noWrap spacing={0} position="right">
              <TokenDisplay
                token={{
                  chain: chain,
                  address: token.address,
                  virtual: token.tokenSpecEnum.toLowerCase() === "virtual",
                  name:
                    token.symbol +
                    (tokenSymbolCountHash[token.symbol] > 1
                      ? `(${shrink(token.address, 3, 2)})`
                      : ""),
                }}
                browser={tokenLinkType[token.address] === "external"}
                iconSize={
                  token.tokenSpecEnum?.toLowerCase() === "virtual" ? 15 : 16
                }
                labelList={addressToLabels?.[token.address]}
                showMoreLabelStyle="hover-address"
              ></TokenDisplay>
            </Group>
          </Th>
        ))}
      </tr>
    );

    const Net = function () {
      const currentProcessTokenHash: { [name: string]: IFlowToken } = {};
      [
        ...(totalRevenues ?? []),
        ...(totalCosts ?? []),
        ...(totalProfits ?? []),
      ]?.forEach((net) => {
        net.revenues.forEach((revenue) => {
          currentProcessTokenHash[revenue.token.address] = revenue.token;
        });
      });

      const renderRows = (title: string, nets: Array<IFlowNetProfit>) => {
        const totalProfit = nets[nets.length - 1];

        let newNets = nets
          ?.slice(0, -1)
          ?.sort((profit) => (profit.tag ? -1 : 0));
        if (title === "Profit") {
          newNets = nets;
        }

        const otherTds = (profit: any, netType?: string) => {
          return tokenArr.map((token) => {
            let td = (
              <Td
                width={isSmallScreen ? `${PROFIT_TOKEN_WIDTH}px` : "auto"}
                key={`td-${token.address}`}
              ></Td>
            );
            profit.revenues.forEach((revenue: any, idx: number) => {
              if (revenue.token.address === token.address) {
                td = (
                  <Td
                    width={isSmallScreen ? `${PROFIT_TOKEN_WIDTH}px` : "auto"}
                    align="right"
                    key={`td-${token.address}`}
                  >
                    <AmountDisplay
                      value={parseFloat(
                        revenue.amountDecimal || revenue.amount
                      )}
                      digits={6}
                      hideZero
                      style={{
                        color:
                          theme.colorScheme === "dark"
                            ? theme.white
                            : theme.colors.dark[4],
                        fontWeight: netType === "total" ? "bold" : "",
                      }}
                    />
                  </Td>
                );
              }
            });
            return td;
          });
        };

        const backgroundColor =
          theme.colorScheme === "dark"
            ? theme.colors.dark[title === "Profit" ? 5 : 4]
            : theme.colors.gray[title === "Profit" ? 4 : 2];

        const totalTr = (
          <tr
            style={{
              backgroundColor,
            }}
          >
            {txType?.toLowerCase() !== "sandwich" &&
            txType?.toLowerCase() !== "jit" ? null : (
              <Td width={isSmallScreen ? `${PROFIT_TAG_WIDTH}px` : "auto"}></Td>
            )}
            <Td width={isSmallScreen ? `${PROFIT_ADDRESS_WIDTH}px` : "auto"}>
              <Text
                size="xs"
                sx={(theme) => ({
                  color:
                    theme.colorScheme === "dark"
                      ? theme.white
                      : theme.colors.dark[4],
                  fontWeight: "bold",
                })}
              >
                {title}
              </Text>
            </Td>
            {otherTds(totalProfit, "total")}
          </tr>
        );

        return (
          <>
            {totalTr}
            {(newNets as Array<IFlowNetProfit>)?.map((profit, idx) => {
              return (
                <tr key={`tr-nets-${idx}-${profit.address}`}>
                  {txType?.toLowerCase() === "sandwich" ? (
                    <Td
                      width={isSmallScreen ? `${PROFIT_TAG_WIDTH}px` : "auto"}
                    >
                      {profit?.tag && (
                        <Group sx={() => ({ alignItems: "center" })}>
                          {
                            {
                              attacker: (
                                <Tooltip
                                  label="Attacker"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <Icon name="icon-role" color="#F64E60" />
                                  </Box>
                                </Tooltip>
                              ),
                              victim: (
                                <Tooltip
                                  label="Victim"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <Icon
                                      name="icon-role"
                                      color={dark ? "#B5B5C3" : "#7E8299"}
                                    />
                                  </Box>
                                </Tooltip>
                              ),
                              middleattacker: (
                                <Tooltip
                                  label="MiddleAttacker"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <Icon name="icon-role" color="#8667BF" />
                                  </Box>
                                </Tooltip>
                              ),
                              miner: (
                                <Tooltip
                                  label={
                                    chain.toLowerCase() === "bsc"
                                      ? "Validator"
                                      : "Builder"
                                  }
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <ImHammer
                                      style={{
                                        color: "#54B498",
                                        fontSize: 19,
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              ),
                              gasfee: (
                                <Tooltip
                                  label="Gas Fee"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <FaGasPump
                                      style={{
                                        color: "#383838",
                                        fontSize: 19,
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              ),
                            }[
                              profit?.netType?.toLowerCase() ||
                                profit?.tag?.toLowerCase()
                            ]
                          }
                        </Group>
                      )}
                    </Td>
                  ) : null}
                  {txType?.toLowerCase() === "jit" ? (
                    <Td
                      width={isSmallScreen ? `${PROFIT_TAG_WIDTH}px` : "auto"}
                    >
                      {profit?.tag && (
                        <Group sx={() => ({ alignItems: "center" })}>
                          {
                            {
                              initiator: (
                                <Tooltip
                                  label="Initiator"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <Icon name="icon-role" color="#F64E60" />
                                  </Box>
                                </Tooltip>
                              ),
                              affected: (
                                <Tooltip
                                  label="Affected"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <Icon
                                      name="icon-role"
                                      color={dark ? "#B5B5C3" : "#7E8299"}
                                    />
                                  </Box>
                                </Tooltip>
                              ),
                              miner: (
                                <Tooltip
                                  label={
                                    chain.toLowerCase() === "bsc"
                                      ? "Validator"
                                      : "Builder"
                                  }
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <ImHammer
                                      style={{
                                        color: "#54B498",
                                        fontSize: 19,
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              ),
                              gasfee: (
                                <Tooltip
                                  label="Gas Fee"
                                  withArrow
                                  position="right"
                                >
                                  <Box>
                                    <FaGasPump
                                      style={{
                                        color: "#383838",
                                        fontSize: 19,
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              ),
                            }[
                              profit?.netType?.toLowerCase() ||
                                profit?.tag?.toLowerCase()
                            ]
                          }
                        </Group>
                      )}
                    </Td>
                  ) : null}
                  <Td
                    width={isSmallScreen ? `${PROFIT_ADDRESS_WIDTH}px` : "auto"}
                  >
                    <Group noWrap={!isSmallScreen} spacing={6}>
                      {profit.address ? (
                        <ContractDisplay
                          chain={chain}
                          address={profit.address}
                          lpIcon={lpIcons?.[profit.address]}
                          labelList={[
                            ...(profit.netType
                              ? [
                                  profit.netType
                                    .replace(/([A-Z])/g, " $1")
                                    .trim(),
                                ]
                              : []),
                            ...(addressToLabels?.[profit.address] ?? []),
                          ]}
                          {...{
                            [addressLinkType[profit.address]]: true,
                          }}
                        />
                      ) : null}

                      {builderMap[profit.address.toLowerCase()] ? (
                        <EpBadge
                          text={builderMap[profit.address.toLowerCase()]}
                        />
                      ) : null}
                    </Group>
                  </Td>
                  {otherTds(profit)}
                </tr>
              );
            })}
          </>
        );
      };

      return (
        <>
          <tbody
            style={{
              borderTop:
                theme.colorScheme === "dark" ? "none" : "1px solid #e9ecef",
            }}
          >
            {renderRows("Revenue", totalRevenues ?? [])}
            {renderRows("Cost", totalCosts ?? [])}
            {renderRows("Profit", totalProfits ?? [])}
          </tbody>
        </>
      );
    };

    const headerTable = (
      <Table
        highlightOnHover={!isSmallScreen}
        horizontalSpacing={20}
        style={{
          tableLayout: "fixed",
          width: size.width || "100%",
          background: dark ? "#25262b" : "#f1f3f5",
        }}
      >
        <colgroup>
          {txType?.toLowerCase() === "sandwich" ||
          txType?.toLowerCase() === "jit" ? (
            <col style={{ width: PROFIT_TAG_WIDTH }}></col>
          ) : null}
          <col style={{ width: PROFIT_ADDRESS_WIDTH }}></col>
          {tokenArr.map((token) => (
            <col
              key={token.address}
              style={{
                width: token.address.includes("virtual")
                  ? VIRTUAL_PROFIT_TOKEN_WIDTH
                  : PROFIT_TOKEN_WIDTH,
              }}
            ></col>
          ))}
        </colgroup>
        <thead>{headers}</thead>
      </Table>
    );

    return (
      <div className="tx-flow-chart">
        <div key={`table-`} style={{ width: "100%" }}>
          {txType?.toLowerCase() !== "sandwich" &&
            txType?.toLowerCase() !== "jit" && <Space h="lg" />}
          <Box
            style={{
              padding: 0,
              width: "100%",
            }}
            ref={size.ref}
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[7]
                  : theme.white,
            })}
          >
            {isHeaderFixed ? (
              <>
                <Affix
                  ref={headerRef}
                  sx={{
                    width: size.width || "100%",
                    overflow: "auto",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                  position={{ top: 80, right: isSmallScreen ? 0 : 24 }}
                  onScrollCapture={() => debounce(handleOnScrollCapture(0))}
                >
                  {headerTable}
                </Affix>
                <Box sx={{ height: 40 }} />
              </>
            ) : null}
            <Box
              sx={{ overflow: "auto" }}
              ref={tableRef}
              onScrollCapture={() => debounce(handleOnScrollCapture(1))}
            >
              {!isHeaderFixed ? headerTable : null}
              <Table
                highlightOnHover={!isSmallScreen}
                horizontalSpacing={20}
                style={{ tableLayout: "fixed" }}
              >
                <colgroup>
                  {txType?.toLowerCase() === "sandwich" ||
                  txType?.toLowerCase() === "jit" ? (
                    <col style={{ width: PROFIT_TAG_WIDTH }}></col>
                  ) : null}
                  <col style={{ width: PROFIT_ADDRESS_WIDTH }}></col>
                  {tokenArr.map((token, idx) => (
                    <col
                      key={token.address + idx}
                      style={{
                        width: token.address.includes("virtual")
                          ? VIRTUAL_PROFIT_TOKEN_WIDTH
                          : PROFIT_TOKEN_WIDTH,
                      }}
                    ></col>
                  ))}
                </colgroup>
                {tbodys}
                {totalRevenues ? <Net /> : null}
              </Table>
            </Box>
          </Box>
        </div>
      </div>
    );
  } else {
    return <EpAlert>Data Not Found!</EpAlert>;
  }
});
